// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
import axios, { AxiosResponse } from 'axios';
import { MessagePlugin } from 'tdesign-vue-next';

import router from '@/router';
import type { TokenResponse } from '@/types/business/auth';
import type { BaseResponse } from '@/types/common/http';
import { getRefreshToken, getToken, removeRefreshToken, removeToken, setRefreshToken, setToken } from '@/utils/auth';

const pendingRequests: Array<{
  config: any;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

let isRefreshing = false; // 是否正在登录中，防止重复跳转

const REFRESH_URL = '/auth/refresh';
const SUCCESS_CODES = [200, 1073741824];

let refreshPromise: Promise<void> | null = null;

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('缺少刷新令牌');
  }

  const rsp = await request.post<BaseResponse<TokenResponse>>(REFRESH_URL, null, {
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
  });

  const { code, data, msg } = rsp.data;

  if (code !== 200 || !data) {
    throw new Error(msg || '刷新令牌失败');
  }

  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
};

const redirectToLogin = () => {
  const redirect = router.currentRoute.value.path;
  if (redirect !== '/login') {
    router.push({ path: '/login', query: { redirect } });
  } else {
    router.push('/login');
  }
};

export const request = axios.create({
  baseURL: '/api/v1',
});

request.interceptors.request.use((config) => {
  const token = getToken(); // 从本地存储获取 token
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

request.interceptors.response.use(
  async (rsp: AxiosResponse) => {
    if (rsp.status !== 200) {
      MessagePlugin.error(rsp.data.msg || '未知错误');
      return Promise.reject(new Error(rsp.data.msg || '未知错误'));
    }
    // 判断是否为文件流响应
    const contentType = rsp.headers['content-type'];

    if (
      contentType?.includes('application/octet-stream') ||
      contentType?.includes('application/vnd.ms-excel') ||
      contentType?.includes('application/vnd.openxmlformats-officedocument') ||
      contentType?.includes('application/pdf')
    ) {
      // 将arraybuffer转换为blob
      const blob = new Blob([rsp.data], { type: contentType });
      return blob;
    }

    const { code } = rsp.data;
    if (rsp.data instanceof ArrayBuffer) {
      try {
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(rsp.data);
        const jsonData = JSON.parse(text);
        rsp.data = jsonData;
      } catch (error) {
        console.error('无法将ArrayBuffer转换为JSON:', error);
        // 如果转换失败，保持原样
      }
    }

    if (code === 401003) {
      // 刷新 token
      if (rsp.config.url?.includes(REFRESH_URL)) {
        // 刷新接口本身失败，直接退出登录
        removeToken();
        removeRefreshToken();
        redirectToLogin();
        return Promise.reject(new Error(rsp.data.msg || '登录已过期，请重新登录'));
      }

      const retryRequest = new Promise((resolve, reject) => {
        pendingRequests.push({
          config: rsp.config,
          resolve,
          reject,
        });
      });

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken()
          .then(async () => {
            await retryPendingRequests();
          })
          .catch((error) => {
            // 刷新失败，清理等待中的请求并跳转登录
            const requests = [...pendingRequests];
            pendingRequests.length = 0;
            requests.forEach(({ reject }) => reject(error));

            removeToken();
            removeRefreshToken();
            redirectToLogin();
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      return retryRequest;
    }

    const codeStr = String(code);
    if (codeStr.length === 6 && codeStr.startsWith('401')) {
      MessagePlugin.error(rsp.data.msg || '未知错误');

      // 如果是登录请求,直接抛出错误
      if (rsp.config.url?.includes('/login')) {
        return Promise.reject(new Error(rsp.data.msg || '未知错误'));
      }

      // 存储当前请求
      const retryRequest = new Promise((resolve, reject) => {
        pendingRequests.push({
          config: rsp.config,
          resolve,
          reject,
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;
        removeToken();
        removeRefreshToken();
        const redirect = router.currentRoute.value.path;
        if (redirect !== '/login') {
          router.push({ path: '/login', query: { redirect } });
        } else {
          router.push('/login');
        }
      }

      return retryRequest; // 返回一个等待重试的 Promise
    }

    if (code === 403) {
      console.warn('请求403');
      MessagePlugin.error('没有访问权限');
      router.push('/result/403');
      return Promise.reject(new Error('没有访问权限'));
    }

    if (code === 40405) {
      MessagePlugin.error(rsp.data.msg || '未知错误');
      return Promise.reject(rsp.data);
    }

    if (code === 620) {
      // 统一转换为 Error，业务层可按现有失败处理方式读取后端 msg。
      return Promise.reject(new Error(rsp.data.msg || '未知错误'));
    }

    if (code === 40117) {
      return Promise.reject(rsp.data);
    }

    if (!SUCCESS_CODES.includes(code)) {
      MessagePlugin.error(rsp.data.msg || '未知错误');

      return Promise.reject(new Error(rsp.data.msg || '未知错误'));
    }
    return Promise.resolve(rsp) as any;
  },
  (error) => {
    MessagePlugin.error('请求失败，请检查网络连接');
    return Promise.reject(error);
  },
);

export const retryPendingRequests = async () => {
  isRefreshing = false;

  // 重新发送所有等待中的请求
  const requests = [...pendingRequests];
  pendingRequests.length = 0;

  const promises = requests.map(async ({ config, resolve, reject }) => {
    try {
      // 重试时确保使用最新 access token（避免复用旧 config 里的 Authorization）
      const token = getToken();
      const nextConfig = { ...config, headers: { ...(config?.headers || {}) } };
      if (token && !nextConfig.url?.includes(REFRESH_URL)) {
        (nextConfig.headers as any).Authorization = `Bearer ${token}`;
      }

      const response = await request(nextConfig);
      resolve(response);
    } catch (err) {
      reject(err);
    }
  });

  await Promise.allSettled(promises);
};
